import './style.scss';

// Import dependencies and utilities
import Sortable from 'sortablejs';
import Actions from '@automator/builder/_legacy/js/item/actions';
import { restCall } from '@automator/builder/_legacy/js/common/utilities';
import { getActionsOrder } from '@automator/builder/_legacy/js/item/sortability/utilities';
import { sanitizeConditionsForMeta, getConditionsGroupById } from '@automator/builder/_legacy/js/item/conditions/utilities';

/**
 * Makes the actions and conditions block sortable
 */
export default class MakeSortable {
	/**
	 * Used to know if the user is currently dragging actions or conditions blocks
	 * @type {Boolean}
	 */
	isSorting = false;

	/* eslint-disable-next-line require-jsdoc */
	constructor() {
		// Do actions and conditions block sortable
		this.createDropzones();
	}

	/**
	 * Iterates the wrappers that contain sortable nodes and creates instances of `Sortable`
	 */
	createDropzones() {
		this.dropzones = {
			instant: [],
			delayed: [],
			scheduled: []
		}

		Object.entries( this.dropzonesSelectors.action ).forEach( ( [ actionTypeID, selectors ] ) => {
			// Iterate selectors
			selectors.forEach( ( selector ) => {
				// Iterate wrappers
				document.querySelectorAll( `.${ selector }` ).forEach( ( $wrapper ) => {
					// Decide if users can sort INSIDE the list
					const canSortSiblings = actionTypeID === 'instant' ? true : false;
					
					// Save instance
					this.dropzones[ actionTypeID ].push(
						new Sortable(
							$wrapper, 
							Object.assign( this.settings, {
								sort: canSortSiblings
							} ) 
						)
					);
				} );
			} );
		} );
	}

	/**
	 * Enables or disables a dropzone by group ID
	 * 
	 * @param {String} groupID The group ID (instant/delayed/scheduled)
	 */
	setDisableStatusDropzone( groupID, status ) {
		this.dropzones[ groupID ].forEach( ( SortableInstance ) => {
			// Disable it
			SortableInstance.option( 'disabled', status );
		} );
	}

	/**
	 * Disables or enables all dropzones
	 */
	setDisableStatusAllDropzones( status, { exceptGroupdID } ) {
		Object.keys( this.dropzones ).forEach( ( groupID ) => {
			// Check if we should ignore this one
			if ( groupID === exceptGroupdID ) {
				return;
			}

			this.setDisableStatusDropzone( groupID, status );
		} );
	}

	/**
	 * Handles the data modifications after an action or conditions block are dropped
	 *
	 * @param {SortableEvent} event Sortable event
	 */
	handleChanges( event ) {
		// Prepare the data we'll send in the request
		const changes = {
			// Get the new order of the actions
			// As this will be triggered only if either an action node or a group of conditions
			// that contain actions was moved, we don't have to check the object to verify if
			// the index of the IDs changed
			order: getActionsOrder(),

			// The conditions groups. We'll get this below
			conditions: null
		}

		// Send the conditions only if the user is using Pro
		if ( UncannyAutomatorBackend._site.automator.is_pro_active ) {
			// Check some data we'll need later to decide what to do
			// Check if an item or conditions group was moved
			const movedNodeName = event.item.classList.contains( 'uap-item' ) ? 'item' : 'conditions-block';

			// Check if the conditions changed
			// For that, start by checking if the user moved an item (action, in this case).
			// If they moved an conditions block, it means the conditions haven't changed.
			if ( movedNodeName === 'item' ) {
				// Check if the parent of the moved item changed
				// If it didn't change, it means the conditions group didn't change either (if it was inside one)
				const parentNodeChanged = event.to !== event.from;

				if ( parentNodeChanged ) {
					// Check if the item was inside a conditions block
					const itemInsideConditionsBlock = {
						before: event.from.classList.contains( 'uap-conditions-block-items' ),
						after: event.to.classList.contains( 'uap-conditions-block-items' )
					}

					// Get the item ID
					const itemID = event.item.itemId;

					// If the item was inside a conditions group, we have to remove the action from it
					if ( itemInsideConditionsBlock.before ) {
						// Get the conditions block ID
						const conditionsBlockID = event.from.closest( '.uap-conditions-block' ).dataset.id;

						// Get the conditions block
						const conditionsBlock = getConditionsGroupById( { conditionsGroupId: conditionsBlockID } );

						// Remove action
						conditionsBlock.removeAction( itemID );
					}

					// If the item is inside a conditions group now, we have to add the action to it
					if ( itemInsideConditionsBlock.after ) {
						// Get the conditions block ID
						const conditionsBlockID = event.to.closest( '.uap-conditions-block' ).dataset.id;

						// Get the conditions block
						const conditionsBlock = getConditionsGroupById( { conditionsGroupId: conditionsBlockID } );

						// Add action
						conditionsBlock.addAction( itemID );
					}
				}
			}

			// Get the conditions object, ready to be saved
			changes.conditions = sanitizeConditionsForMeta();
		}

		// Save changes
		this.saveChanges( changes );
	}

	/**
	 * Saves the data using a REST call
	 *
	 * @param {Object} changes Object with `.order` and `.conditions`
	 */
	saveChanges( changes ) {
		// Prepare data for each call
		let endpoint = 'actions_order';

		const formData = {
			/* eslint-disable-next-line camelcase */
			recipe_id: UncannyAutomator._recipe.recipe_id,
		}

		// If it's pro, also save the conditions
		if ( UncannyAutomatorBackend._site.automator.is_pro_active ) {
			endpoint = 'actions_order_and_conditions';
			formData.order = changes.order;
			formData.conditions = JSON.stringify( changes.conditions );
		} else {
			// In Free, just save the order
			/* eslint-disable-next-line camelcase */
			formData.actions_order = changes.order;
		}

		// Remove global saving class
		document.body.classList.add( 'uap-sortable-status--saving' );

		// Do REST call
		restCall(
			endpoint,
			formData,
			( response ) => {
				// Remove global dragging class
				document.body.classList.remove( 'uap-sortable-status--sorting' );

				// Remove global saving class
				document.body.classList.remove( 'uap-sortable-status--saving' );

				// Remove class to the dropzones
				document.querySelectorAll( '.uap-sortable-dropzone' ).forEach( ( $dropzone ) => {
					$dropzone.classList.remove( 'uap-sortable-dropzone' );
				} );

				// Render the actions with some delay
				setTimeout( () => {
					// Render the actions metabox again
					new Actions();
				}, 300 );
			}
		);
	}

	/**
	 * Sortable options
	 *
	 * @return {Object} The options
	 */
	get settings() {
		return {
			// ms, animation speed moving items when sorting, `0` — without animation
			animation: 300,

			// Appends the cloned DOM Element into the Document's Body
			fallbackOnBody: true,

			// Threshold of the swap zone
			swapThreshold: 0.5,
			// Threshold of the inverted swap zone (will be set to swapThreshold value by default)
			// Read https://github.com/SortableJS/Sortable/wiki/Swap-Thresholds-and-Direction
			invertedSwapThreshold: true,

			// The direction option is the final option, but the most important. It defines the direction we want to sort in. Think of it as setting the “axis” of the Sortable to either x or y. If we are using the y axis, we set the option to "vertical". If it’s the x axis, we set the option to "horizontal". The direction is needed for Sortable to determine whether the swap zones take up the width of the target, or the height. 
			direction: 'vertical',

			// px, distance mouse must be from empty sortable to insert drag element into it
			emptyInsertThreshold: 0,

			// Class name for the drop placeholder
			ghostClass: 'uap-item-drag-ghost',
			// Class name for the chosen item
			chosenClass: 'uap-item-drag-status-chosen', 

			// Class added to the clone that follows the map
			dragClass: 'uap-item-drag-status-drag-floating',
			// forceFallback: true,

			// Drag handle selector within list items
			handle: '.uap-item-drag-handle',

			// Specifies which items inside the element should be draggable
			draggable: '.uap-conditions-block.uap-conditions-block--sortable, .uap-item.uap-item--sortable',

			// Triggered when the dragging starts
			onStart: ( event ) => {
				this.isSorting = true;

				// Get data about the element being dragged
				const elementData = this.getWhatIsBeingDragged( event.item );

				// Disable all the irrelevant dropzones
				// If it's a conditions block, or an instant action, just leave the "Instant" dropzones
				if ( elementData.is === 'conditions-block' || ( elementData.is === 'action' && elementData.type === 'instant' ) ) {
					this.setDisableStatusAllDropzones( true, {
						exceptGroupdID: 'instant'
					} );
				} else {
					// If we're here, it's because it's either a delayed or scheduled action
					this.setDisableStatusAllDropzones( true, {
						exceptGroupdID: elementData.type
					} );
				}

				// Add a class to style the elements with a delay
				setTimeout( () => {
					// Check if the user is still sorting, and compress the layout
					if ( this.isSorting ) {
						// Add global sorting class
						document.body.classList.add( 'uap-sortable-status--sorting' );

						// Add classes to the dropzones
						const dropzones = elementData.is === 'action' ? this.dropzonesSelectors.action[ elementData.type ] : this.dropzonesSelectors[ 'conditions-block' ];

						dropzones.forEach( ( dropzoneSelector ) => {
							// Get the dropzones. NOTE: This is prepending a dot because all the selectors are classes
							const $dropzones = document.querySelectorAll( `.${ dropzoneSelector }` );

							// Iterate dropzones
							$dropzones.forEach( ( $dropzone ) => {
								$dropzone.classList.add( 'uap-sortable-dropzone' );
							} );
						} );
					}
				}, 300 );
			},

			// Element dragging ended
			onEnd: ( event ) => {
				this.isSorting = false;

				// Check if it really changed
				// We could use .onUpdate AND .onAdd to get the same behavior
				const parentChanged = event.to !== event.from;
				const indexChanged  = event.oldIndex !== event.newIndex; 

				if ( parentChanged || indexChanged ) {
					this.handleChanges( event );

					// Stop here, don't update the UI back
					return;
				}

				// Enable all dropzones again
				this.setDisableStatusAllDropzones( false, {} );

				// Remove global dragging class
				document.body.classList.remove( 'uap-sortable-status--sorting' );

				// Remove class to the dropzones
				document.querySelectorAll( '.uap-sortable-dropzone' ).forEach( ( $dropzone ) => {
					$dropzone.classList.remove( 'uap-sortable-dropzone' );
				} );
			},

			// or { name: "...", pull: [true, false, 'clone', array], put: [true, false, array] }
			group: {
				name: 'nested',
				// This callback will prevent Automator from adding conditions groups inside other conditions groups
				// This will be invoked every time a sortable node is dragged
				pull: ( to, from, $draggedNode ) => {
					// Get data about the element being dragged
					const elementData = this.getWhatIsBeingDragged( $draggedNode );

					// Get the dropzones
					const dropzones = elementData.is === 'action' ? this.dropzonesSelectors.action[ elementData.type ] : this.dropzonesSelectors[ 'conditions-block' ];

					// Check if the user is trying to drop this action inside a valid dropzone
					return dropzones.some( ( selector ) => {
						return to.el.classList.contains( selector );
					} );
				}
			}
		}
	}

	/**
	 * Gets what's being dragged right now (conditions block, or action)
	 * 
	 * @param {Node} $node The Node being dragged
	 * 
	 * @return {String} The element type
	 */
	getWhatIsBeingDragged( $node ) {
		// Response
		const response = {
			is: 'action'
		}

		// Check if it's a conditions block
		if ( $node.classList.contains( 'uap-conditions-block' ) ) {
			response.is = 'conditions-block';
			
			// Stop here
			return response;
		}

		// Get the action
		const action = UncannyAutomator.recipe.items[ $node.dataset.id ];

		// Get the recipe type
		// It can be either 'instant', 'delayed', 'scheduled' or 'custom'
		response.type = action.isDelayed ? 'delayed' : ( action.isScheduled ? 'scheduled' : ( action.isDelayedCustom ? 'delayed-custom' : 'instant' ) );

		return response;
	}

	/**
	 * Returns the dropdowns selectors by element type
	 * 
	 * @return {Object} The dropzones
	 */
	get dropzonesSelectors() {
		return {
			'conditions-block': [
				'uap-items--actions-instant'
			],
			action: {
				instant: [
					'uap-conditions-block-items--instant',
					'uap-items--actions-instant'
				],
				delayed: [
					'uap-conditions-block-items--delayed',
					'uap-items--actions-delayed'
				],
				scheduled: [
					'uap-conditions-block-items--scheduled',
					'uap-items--actions-scheduled'
				]
			}
		}
	}
}
